import { Dimmer, Loader, Segment } from '@mairistem/composants';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { MODES_REGLEMENTS } from '@/constants';
import { ModeReglementContext, RegieContext } from '@/contexts';
import { useAccueilData, useEcheances } from '@/hooks';
import { useRegieStore } from '@/zustand';
import useIndicateursStore from '@/zustand/accueil/useIndicateursStore';

import {
  TileChiffresJour,
  TileChiffresMois,
  TileCycles,
  TileEcheances,
  TileEncaisse,
  TileNomRegie,
} from './tiles';

import './Accueil.less';

const Accueil = () => {
  const regie = useRegieStore((state) => state.regie);
  const { regies } = React.useContext(RegieContext);
  const { regieModesReglement } = React.useContext(ModeReglementContext);

  // Hook centralisé pour les données
  const { encaisse, indicateurs, cycles, recuStats, loading } = useAccueilData();

  // Hook pour le calcul des échéances
  const { echeanceArrete, echeanceVersement } = useEcheances();

  // Calcul des flags pour TileChiffresJour
  const hasPaiementInternet = React.useMemo(
    () => _.some(regieModesReglement, { code: MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET }),
    [regieModesReglement],
  );

  const hasPrelevementInternet = React.useMemo(
    () => _.some(regieModesReglement, { code: MODES_REGLEMENTS.PRELEVEMENT_INTERNET }),
    [regieModesReglement],
  );

  // Heure de dernière interrogation MFO (timestamp du dernier fetch API)
  const cacheTimestamp = useIndicateursStore((state) => state.cache.timestamp);
  const heureInterrogationMfo = cacheTimestamp ? dayjs(cacheTimestamp).format('HH[h]mm') : null;

  // Chargement initial : tant qu'on n'a pas la régie et la liste des régies
  const isInitialLoading = !regie || _.isNil(regies);

  if (isInitialLoading) {
    return (
      <Segment basic secondary className="accueil-dashboard">
        <Dimmer active inverted>
          <Loader inverted>Chargement...</Loader>
        </Dimmer>
      </Segment>
    );
  }

  return (
    <Segment basic secondary className="accueil-dashboard">
      <div className="accueil-dashboard-grid">
        {/* Ligne 1 - Régie + Chiffres (même hauteur) */}
        <div className="accueil-row-top">
          <TileNomRegie />
          <div className="accueil-row-top-right">
            <TileChiffresMois
              indicateurs={indicateurs}
              cycles={cycles}
              loading={loading.indicateurs || loading.cycles}
            />
            <TileChiffresJour
              indicateurs={indicateurs}
              recuStats={recuStats}
              hasPaiementInternet={hasPaiementInternet}
              hasPrelevementInternet={hasPrelevementInternet}
              heureInterrogationMfo={heureInterrogationMfo}
              loading={loading.indicateurs || loading.recuStats}
            />
          </div>
        </div>

        {/* Ligne 2 - Échéances/Encaisse + Cycles */}
        <div className="accueil-row-bottom">
          <div className="accueil-row-bottom-left">
            <TileEcheances
              echeanceArrete={echeanceArrete}
              echeanceVersement={echeanceVersement}
              loading={loading.encaisse}
            />
            <TileEncaisse encaisse={encaisse} loading={loading.encaisse} />
          </div>
          <TileCycles cycles={cycles} loading={loading.cycles} />
        </div>
      </div>
    </Segment>
  );
};

export default Accueil;
